import {useEffect, useRef, ReactNode} from 'react'
import classNames from 'classnames'

import {stringSplitAndJoin} from 'utils'

interface MenuProps<T> {
  children: ReactNode
  isVisible: boolean
  menuClasses?: string
  options: {name: string; value: T; icon: ReactNode; classes?: string}[]
  optionClassesList?: string[]
  onOptionSelect: (value: T) => void
  onClose: () => void
}

export const Menu = <T,>({
  children,
  isVisible,
  menuClasses = '',
  options,
  onOptionSelect,
  onClose,
}: MenuProps<T>) => {
  const ref = useRef<HTMLDivElement>(null)

  const handleOutsideClick = (event: MouseEvent): void => {
    if (isVisible && !ref.current?.contains(event.target as Node)) {
      onClose()
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [isVisible])

  return (
    <div className="relative" ref={ref}>
      <div
        className={classNames(
          `absolute bg-dope-black flex flex-col mx-auto ${menuClasses}`,
          {
            hidden: !isVisible,
          },
        )}
      >
        {options.map(({name, value, icon, classes}) => (
          <button
            key={`button-with-dropdown-option-${value}`}
            data-cy={`analytics-dropdown-option-${stringSplitAndJoin(
              name?.toLowerCase(),
            )}`}
            className={classNames('py-1.5 karla text-sm border-0', classes, {
              'first:rounded-t-0.75': isVisible,
            })}
            onClick={event => {
              event.stopPropagation()
              onOptionSelect(value)
            }}
          >
            <span>{icon}</span>
            <span>{name}</span>
          </button>
        ))}
      </div>
      {children}
    </div>
  )
}
